import { Icon } from "@studio3t/generic-components";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { servicesApi } from "@/api/client";
import { queryKeys } from "@/hooks/queryKeys";
import {
  useSchemasByService,
  useServices,
  useTestSuites,
} from "@/hooks/useApi";
import type { ResourceInfo, SchemaEntrySummary, Service } from "@/types/api";
import {
  GENERATOR_STREAM_RESOURCE,
  LLM_PROVIDERS,
  PROVIDER_LABELS,
} from "@/utils/providerCapabilities";
import { schemaCapableServices } from "@/utils/schemaServices";
import { userStorage } from "@/utils/userStorage";
import { isWorkspaceTestsSuite } from "./saveWorkspace";
import { schemaRowId, suiteRowId } from "./stagingModel";

// Data layer for the /newStudio Explorer: tree model, persisted UI state and
// the query hooks. Mirrors the data handling of /studio's ExplorerSidebar.

export interface ExplorerItem {
  id: string;
  name: string;
  icon: Icon;
  // Semibold prefix rendered before the name as "PREFIX - name" — the
  // schema leaves' encoding label (Figma Bridge_Explorer 14397-190443).
  labelPrefix?: string;
  count?: number;
  // Provider of the owning service — set on leaf resources only, so drag
  // eligibility can follow /studio's staging rules (udf = middleware-only).
  provider?: Service["provider"];
  // The resource kind ("collection", "topic", "route", "script", "stream")
  // — set on leaf resources only, named in the AI Helper's snapshot.
  resourceType?: string;
  children?: ExplorerItem[];
  // True while this item's children are still being fetched — the expanded
  // item shows a skeleton row instead of children.
  childrenLoading?: boolean;
  // Set when the children fetch stalled or failed; rendering swaps the
  // skeletons for a small retry action.
  onRetry?: () => void;
}

export interface ExplorerSection {
  id: string;
  label: string;
  count?: number;
  items: ExplorerItem[];
  loading?: boolean;
}

const COLLAPSED_STORAGE_KEY = "new-studio:explorer-collapsed";
const FILTER_STORAGE_KEY = "new-studio:explorer-filter";
const EXPANDED_ITEMS_STORAGE_KEY = "new-studio:explorer-expanded-items";
const COLLAPSED_SECTIONS_STORAGE_KEY = "new-studio:explorer-collapsed-sections";

// A resources fetch that hasn't answered after this long (e.g. a service whose
// database is unreachable) switches from skeletons to a Retry affordance.
const RESOURCES_STALL_TIMEOUT_MS = 10_000;

const PROVIDER_ORDER: Service["provider"][] = [
  "mongodb",
  "postgres",
  "postgres_cdc",
  "kafka",
  "pubsub",
  "http",
  "http_source",
  "udf",
  "generator",
  "scanner",
  "bedrock",
  "anthropic",
  "openai",
];

const SERVICE_ICONS: Record<Service["provider"], Icon> = {
  mongodb: Icon.Database,
  postgres: Icon.Database,
  postgres_cdc: Icon.Database,
  kafka: Icon.Lightning,
  pubsub: Icon.Notification,
  http: Icon.Link,
  http_source: Icon.Connection,
  // Produces its own stream rather than connecting to anything.
  generator: Icon.Lightning,
  scanner: Icon.Database,
  udf: Icon.Folder,
  bedrock: Icon.AI,
  anthropic: Icon.AI,
  openai: Icon.AI,
};

// Per-type resource icons. collection/script/route follow the Figma design;
// topic has no design example, so it mirrors /studio's ExplorerSidebar
// (every non-route resource renders as Collection there).
const RESOURCE_ICONS: Record<string, Icon> = {
  collection: Icon.Collection,
  topic: Icon.Collection,
  script: Icon.Code,
  route: Icon.Link,
  stream: Icon.Lightning,
};

// A generator's resource is a caller-chosen stream label, not something that
// exists to enumerate — the engine rejects the listing outright, so issuing
// it only manufactures a permanent, unfixable Retry state. It still gets one
// child (`GENERATOR_STREAM` below), otherwise there is nothing to drag into a
// pipeline.
const NON_BROWSABLE_PROVIDERS = new Set<Service["provider"]>(["generator"]);

// The single stream a generator offers. The label is free — it is echoed to
// sinks as the `resource` attribute — so this is a starting point the user can
// rename in the pipeline code, not a lookup.
const GENERATOR_STREAM: ResourceInfo[] = [
  { name: GENERATOR_STREAM_RESOURCE, resource_type: "stream" },
];

const SETTLED_GENERATOR_RESOURCES: ServiceResourcesState = {
  resources: GENERATOR_STREAM,
  isLoading: false,
  isError: false,
  refetch: undefined,
};

// ---------------------------------------------------------------------------
// Persisted UI state (panel collapse, filter, expand/collapse sets)
// ---------------------------------------------------------------------------

function readStoredIdSet(key: string): Set<string> {
  try {
    const raw = userStorage.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

function persistIdSet(key: string, ids: Set<string>): void {
  if (ids.size === 0) userStorage.removeItem(key);
  else userStorage.setItem(key, JSON.stringify([...ids]));
}

// Drops ids that no longer exist in the tree (deleted services, renamed
// schema folders...) so storage never accumulates stale entries.
function pruneIdSet(prev: Set<string>, valid: Set<string>): Set<string> {
  const next = [...prev].filter((id) => valid.has(id));
  return next.length === prev.size ? prev : new Set(next);
}

function toggleInSet(prev: Set<string>, id: string): Set<string> {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  return next;
}

// How an item opens or closes while a filter may be on. The filter's own
// expansion is an overlay on `expanded`, never written into it; what the
// user clicks or an AI action expands or collapses under a filter lands in
// the overlay too — open or closed, said outright per item — and goes with
// it when the filter clears, leaving the tree exactly as the user had it.
function useItemExpansion(
  setExpanded: Dispatch<SetStateAction<Set<string>>>,
  filterActive: boolean,
) {
  const [filterOverrides, setFilterOverrides] = useState(
    () => new Map<string, boolean>(),
  );

  useEffect(() => {
    if (!filterActive) setFilterOverrides(new Map());
  }, [filterActive]);

  const override = useCallback((ids: string[], open: boolean) => {
    setFilterOverrides((prev) => {
      const next = new Map(prev);
      for (const id of ids) next.set(id, open);
      return next;
    });
  }, []);

  // A click on a row showing as `shown`: the opposite of that.
  const toggleItem = useCallback(
    (id: string, shown: boolean) =>
      filterActive
        ? override([id], !shown)
        : setExpanded((prev) => toggleInSet(prev, id)),
    [filterActive, override, setExpanded],
  );
  // The AI's expand and collapse: the user's set and the overlay both, so
  // the item shows as asked with or without a filter.
  const expandItems = useCallback(
    (ids: string[]) => {
      setExpanded((prev) => new Set([...prev, ...ids]));
      override(ids, true);
    },
    [override, setExpanded],
  );
  const collapseItems = useCallback(
    (ids: string[]) => {
      setExpanded(
        (prev) => new Set([...prev].filter((id) => !ids.includes(id))),
      );
      override(ids, false);
    },
    [override, setExpanded],
  );

  return { filterOverrides, toggleItem, expandItems, collapseItems };
}

export function useExplorerUiState() {
  const [collapsed, setCollapsed] = useState(
    () => userStorage.getItem(COLLAPSED_STORAGE_KEY) === "true",
  );
  const [filterText, setFilterText] = useState(
    () => userStorage.getItem(FILTER_STORAGE_KEY) ?? "",
  );
  const [expanded, setExpanded] = useState(() =>
    readStoredIdSet(EXPANDED_ITEMS_STORAGE_KEY),
  );
  const [collapsedSections, setCollapsedSections] = useState(() =>
    readStoredIdSet(COLLAPSED_SECTIONS_STORAGE_KEY),
  );
  const filterActive = filterText.trim() !== "";
  const { filterOverrides, toggleItem, expandItems, collapseItems } =
    useItemExpansion(setExpanded, filterActive);

  useEffect(() => {
    userStorage.setItem(COLLAPSED_STORAGE_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    if (filterText) userStorage.setItem(FILTER_STORAGE_KEY, filterText);
    else userStorage.removeItem(FILTER_STORAGE_KEY);
  }, [filterText]);

  useEffect(() => {
    persistIdSet(EXPANDED_ITEMS_STORAGE_KEY, expanded);
  }, [expanded]);

  useEffect(() => {
    persistIdSet(COLLAPSED_SECTIONS_STORAGE_KEY, collapsedSections);
  }, [collapsedSections]);

  const toggleSection = useCallback(
    (id: string) => setCollapsedSections((prev) => toggleInSet(prev, id)),
    [],
  );

  return {
    collapsed,
    setCollapsed,
    filterText,
    setFilterText,
    expanded,
    setExpanded,
    filterOverrides,
    expandItems,
    collapseItems,
    collapsedSections,
    setCollapsedSections,
    toggleItem,
    toggleSection,
  };
}

export type ExplorerUiState = ReturnType<typeof useExplorerUiState>;

// Once the tree is known, drop persisted expand/collapse ids that no longer
// exist. Runs only after services load — pruning against a still-empty tree
// would wipe the stored state.
export function useStoredIdPruning(
  sections: ExplorerSection[],
  treeReady: boolean,
  ui: ExplorerUiState,
) {
  const { setExpanded, setCollapsedSections } = ui;
  useEffect(() => {
    if (!treeReady) return;
    const validItemIds = new Set<string>();
    const collect = (items: ExplorerItem[]) => {
      for (const item of items) {
        validItemIds.add(item.id);
        if (item.children) collect(item.children);
      }
    };
    for (const section of sections) collect(section.items);
    const validSectionIds = new Set(sections.map((s) => s.id));
    setExpanded((prev) => pruneIdSet(prev, validItemIds));
    setCollapsedSections((prev) => pruneIdSet(prev, validSectionIds));
  }, [sections, treeReady, setExpanded, setCollapsedSections]);
}

// ---------------------------------------------------------------------------
// Queries: services, per-service resources, stall/retry tracking
// ---------------------------------------------------------------------------

interface ServiceResourcesState {
  resources: ResourceInfo[] | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: (() => void) | undefined;
}

const PENDING_RESOURCES: ServiceResourcesState = {
  resources: undefined,
  isLoading: true,
  isError: false,
  refetch: undefined,
};

function useServiceList() {
  const { data: services, isLoading } = useServices();
  const serviceList = useMemo(
    () => (services ?? []).filter((svc) => !LLM_PROVIDERS.has(svc.provider)),
    [services],
  );
  return { services, serviceList, servicesLoading: isLoading };
}

// Resources are fetched for every service up-front so the tree can show
// per-service counts (per Figma) and expand instantly; each query is cached
// under the same key /studio uses.
function useServiceResourcesMap(serviceList: Service[]) {
  return useQueries({
    queries: serviceList.map((svc) => ({
      queryKey: queryKeys.serviceResources(svc.name),
      queryFn: () => servicesApi.listResources(svc.name),
      enabled: !NON_BROWSABLE_PROVIDERS.has(svc.provider),
    })),
    combine: (results) =>
      Object.fromEntries(
        serviceList.map((svc, i) => [
          svc.name,
          NON_BROWSABLE_PROVIDERS.has(svc.provider)
            ? SETTLED_GENERATOR_RESOURCES
            : {
                resources: results[i]?.data?.resources,
                isLoading: results[i]?.isLoading ?? true,
                isError: results[i]?.isError ?? false,
                refetch: results[i]?.refetch,
              },
        ]),
      ) as Record<string, ServiceResourcesState>,
  });
}

// Clears timers for services no longer loading; arms one per newly loading
// service that lacks one.
function syncStallTimers(
  timers: Map<string, number>,
  loading: Set<string>,
  armTimer: (serviceName: string) => void,
): void {
  const stale = [...timers.keys()].filter((name) => !loading.has(name));
  for (const name of stale) {
    clearTimeout(timers.get(name));
    timers.delete(name);
  }
  const missing = [...loading].filter((name) => !timers.has(name));
  for (const name of missing) armTimer(name);
}

// Services whose resources fetch has been in flight longer than the stall
// timeout (unreachable database, hung connection). Timers are keyed by
// service so re-renders don't reset the window.
function useStalledServices(
  serviceList: Service[],
  resourcesByService: Record<string, ServiceResourcesState>,
) {
  const [stalledServices, setStalledServices] = useState<Set<string>>(
    new Set(),
  );
  const stallTimersRef = useRef(new Map<string, number>());

  const armTimer = useCallback((serviceName: string) => {
    const timers = stallTimersRef.current;
    clearTimeout(timers.get(serviceName));
    timers.set(
      serviceName,
      window.setTimeout(() => {
        timers.delete(serviceName);
        setStalledServices((prev) => new Set(prev).add(serviceName));
      }, RESOURCES_STALL_TIMEOUT_MS),
    );
  }, []);

  const loadingKey = serviceList
    .filter((svc) => resourcesByService[svc.name]?.isLoading)
    .map((svc) => svc.name)
    .sort()
    .join("|");

  useEffect(() => {
    const loading = new Set(loadingKey ? loadingKey.split("|") : []);
    syncStallTimers(stallTimersRef.current, loading, armTimer);
    // A service that finished loading is no longer stalled.
    setStalledServices((prev) => pruneIdSet(prev, loading));
  }, [loadingKey, armTimer]);

  useEffect(() => {
    const timers = stallTimersRef.current;
    return () => {
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, []);

  const retryServiceResources = useCallback(
    (serviceName: string) => {
      setStalledServices((prev) => {
        const next = new Set(prev);
        next.delete(serviceName);
        return next;
      });
      resourcesByService[serviceName]?.refetch?.();
      armTimer(serviceName);
    },
    [resourcesByService, armTimer],
  );

  return { stalledServices, retryServiceResources };
}

// ---------------------------------------------------------------------------
// Section building
// ---------------------------------------------------------------------------

function buildServiceItem(
  svc: Service,
  resourceState: ServiceResourcesState,
  failed: boolean,
  onRetry: (serviceName: string) => void,
): ExplorerItem {
  const { resources, isLoading } = resourceState;
  return {
    id: `service:${svc.name}`,
    name: svc.name,
    icon: SERVICE_ICONS[svc.provider] ?? Icon.Connection,
    count: resources?.length,
    childrenLoading: isLoading && !failed,
    onRetry: failed ? () => onRetry(svc.name) : undefined,
    children: (resources ?? []).map((r) => ({
      id: `resource:${svc.name}:${r.name}`,
      name: r.name,
      icon: RESOURCE_ICONS[r.resource_type] ?? Icon.Collection,
      provider: svc.provider,
      resourceType: r.resource_type,
    })),
  };
}

function buildProviderSections(
  services: Service[],
  itemFor: (svc: Service) => ExplorerItem,
): ExplorerSection[] {
  const grouped = new Map<Service["provider"], Service[]>();
  for (const svc of services) {
    const list = grouped.get(svc.provider) ?? [];
    list.push(svc);
    grouped.set(svc.provider, list);
  }
  const orderMap = new Map(PROVIDER_ORDER.map((p, i) => [p, i]));
  return [...grouped.entries()]
    .sort(([a], [b]) => (orderMap.get(a) ?? 999) - (orderMap.get(b) ?? 999))
    .map(([provider, svcs]) => ({
      id: `provider:${provider}`,
      label: PROVIDER_LABELS[provider] ?? provider,
      count: svcs.length,
      items: svcs.map(itemFor),
    }));
}

/** One schema store as the explorer's Schemas section shows it. */
export interface SchemaStore {
  serviceName: string;
  schemas: SchemaEntrySummary[] | undefined;
  loading: boolean;
  /** The list fetch failed — the folder offers a retry. */
  error: boolean;
}

// The SCHEMAS section: one folder per schema-capable service (schemas
// live per service, TTTL-319 — every MongoDB and Pub/Sub service has a
// store of its own), each holding that service's stored schemas. A
// section only while some service can store schemas.
export function buildSchemasSection(
  stores: SchemaStore[],
  retry: (serviceName: string) => void,
): ExplorerSection[] {
  if (stores.length === 0) return [];
  const counted = stores.filter((s) => s.schemas !== undefined);
  return [
    {
      id: "schemas",
      label: "Schemas",
      count:
        counted.length > 0
          ? counted.reduce((n, s) => n + (s.schemas?.length ?? 0), 0)
          : undefined,
      // The section's own skeleton only while NO store has answered; a
      // store still loading (an unreachable database times out slowly)
      // keeps its folder's skeleton without hiding the others.
      loading: stores.every((s) => s.loading),
      items: stores.map((store) => ({
        id: `schemas:${store.serviceName}`,
        name: store.serviceName,
        icon: Icon.Folder,
        count: store.schemas?.length,
        childrenLoading: store.loading,
        ...(store.error ? { onRetry: () => retry(store.serviceName) } : {}),
        // Schema leaves per Figma Bridge_Explorer (14397-190443): the
        // Schema icon and the encoding as a semibold "JSON - name" prefix.
        children: (store.schemas ?? []).map((s) => ({
          id: schemaRowId(store.serviceName, s.id),
          name: s.name || s.id,
          icon: Icon.Schema,
          labelPrefix: s.encoding?.toUpperCase(),
        })),
      })),
    },
  ];
}

// The schema stores of every schema-capable service, through one list
// query each.
function useSchemaStores(services: Service[] | undefined): {
  stores: SchemaStore[];
  retry: (serviceName: string) => void;
} {
  const queryClient = useQueryClient();
  const serviceNames = useMemo(
    () => schemaCapableServices(services).map((s) => s.name),
    [services],
  );
  const { byService } = useSchemasByService(serviceNames);
  const stores = useMemo(
    () =>
      serviceNames.map((serviceName) => {
        const q = byService[serviceName];
        return {
          serviceName,
          schemas: q?.data,
          loading: q?.isLoading ?? true,
          error: q?.isError ?? false,
        };
      }),
    [serviceNames, byService],
  );
  const retry = useCallback(
    (serviceName: string) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.schemas(serviceName),
      });
    },
    [queryClient],
  );
  return { stores, retry };
}

// The TEST SUITES section lists the stored CATALOG suites — one leaf row
// per suite, its badge counting the suite's cases (Figma Bridge_Explorer
// 14784-219168). Workspace test snapshots (suites a workspace save
// writes, marked by description) stay out: the tree shows the same shared
// suites no matter which workspace is open. Each row drags into the Tests
// panel, which stages ALL the suite's cases as this pipeline's own set.
function useSuiteRows(): {
  suites: Array<{ id: string; name: string; caseCount: number }>;
  loading: boolean;
} {
  const { data: suites, isLoading } = useTestSuites();
  const suiteRows = (suites ?? [])
    .filter((suite) => !isWorkspaceTestsSuite(suite))
    .map((suite) => ({
      id: suite.id,
      name: suite.name,
      // The list summary's own count — the full suites (with their cases)
      // load on demand, in the hover preview and on drop.
      caseCount: suite.case_count,
    }));
  return { suites: suiteRows, loading: isLoading };
}

function buildTestsSection(
  suites: Array<{ id: string; name: string; caseCount: number }>,
  loading: boolean,
): ExplorerSection {
  return {
    id: "test-suites",
    label: "Test Suites",
    count: suites.length,
    loading,
    items: suites.map((suite) => ({
      id: suiteRowId(suite.id),
      name: suite.name,
      // The checklist glyph, not the single-line Test — the suite rows
      // match the panel's suite header (Figma Bridge_Explorer 14784-219168).
      icon: Icon.Tests,
      count: suite.caseCount,
    })),
  };
}

// ---------------------------------------------------------------------------
// The one hook the Explorer component consumes
// ---------------------------------------------------------------------------

export function useExplorerSections() {
  const { services, serviceList, servicesLoading } = useServiceList();
  const resourcesByService = useServiceResourcesMap(serviceList);
  const { stalledServices, retryServiceResources } = useStalledServices(
    serviceList,
    resourcesByService,
  );

  const { stores: schemaStores, retry: retrySchemas } =
    useSchemaStores(services);
  const { suites: studioSuites, loading: testsLoading } = useSuiteRows();

  const sections = useMemo(() => {
    const itemFor = (svc: Service) =>
      buildServiceItem(
        svc,
        resourcesByService[svc.name] ?? PENDING_RESOURCES,
        stalledServices.has(svc.name) ||
          (resourcesByService[svc.name]?.isError ?? false),
        retryServiceResources,
      );
    return [
      ...buildProviderSections(serviceList, itemFor),
      ...buildSchemasSection(schemaStores, retrySchemas),
      buildTestsSection(studioSuites, testsLoading),
    ];
  }, [
    serviceList,
    resourcesByService,
    stalledServices,
    retryServiceResources,
    schemaStores,
    retrySchemas,
    studioSuites,
    testsLoading,
  ]);

  return {
    sections,
    servicesLoading,
    hasServices: serviceList.length > 0,
  };
}
